import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ZoomSchedulerProps {
  formData: {
    name: string;
    email: string;
    company: string;
    interest: string;
    message: string;
  };
  onMeetingCreated: (meetingData: any) => void;
  onBack: () => void;
}

interface ZoomMeetingResponse {
  id: string;
  topic: string;
  start_time: string;
  duration: number;
  timezone: string;
  join_url: string;
  password: string;
}

const ZoomScheduler = ({ formData, onMeetingCreated, onBack }: ZoomSchedulerProps) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  // Generate available time slots (9 AM to 5 PM, 30-minute intervals)
  const timeSlots = [];
  for (let hour = 9; hour < 17; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeSlots.push(time);
    }
  }

  const handleScheduleMeeting = async () => {
    // Legacy flow disabled: direct Zoom scheduling now runs server-side via /book-demo triage.
    toast({
      title: 'Please use the demo request flow',
      description: 'Meetings are now confirmed by our team from your requested slots. Visit /book-demo to submit your preferred times.',
      variant: 'destructive',
    });
    onBack();
    return;
    // eslint-disable-next-line no-unreachable
    void (async () => {
    if (!selectedDate || !selectedTime) {
      toast({
        title: "Please select date and time",
        description: "Both date and time are required to schedule the meeting.",
        variant: "destructive"
      });
      return;
    }

    setIsCreating(true);

    try {
      // Combine date and time
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const meetingDateTime = new Date(selectedDate);
      meetingDateTime.setHours(hours, minutes, 0, 0);

      // Create the meeting data
      const meetingData = {
        topic: `Discovery Call with ${formData.name}`,
        start_time: meetingDateTime.toISOString(),
        duration: 30,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        attendee_email: formData.email,
        attendee_name: formData.name
      };

      console.log('Creating Zoom meeting:', meetingData);

      // Call the edge function to create Zoom meeting
      const { data: zoomMeeting, error: zoomError } = await supabase.functions.invoke('create-zoom-meeting', {
        body: meetingData
      });

      if (zoomError) {
        console.error('Error creating Zoom meeting:', zoomError);
        throw zoomError;
      }

      console.log('Zoom meeting created:', zoomMeeting);

      // Call the meeting confirmation email function
      const emailData = {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        interest: formData.interest,
        message: formData.message,
        meetingDetails: {
          date: format(meetingDateTime, 'EEEE, MMMM do, yyyy'),
          time: format(meetingDateTime, 'h:mm a'),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          joinUrl: zoomMeeting.join_url,
          meetingId: zoomMeeting.id,
          password: zoomMeeting.password
        }
      };

      const { error: emailError } = await supabase.functions.invoke('send-meeting-confirmation', {
        body: emailData
      });

      if (emailError) {
        console.error('Error sending confirmation email:', emailError);
        // Don't fail the meeting creation if email fails
        toast({
          title: "Meeting created successfully!",
          description: "Note: There was an issue sending the confirmation email, but your meeting is scheduled.",
          variant: "default"
        });
      }

      // Pass the meeting data back to parent
      onMeetingCreated({
        ...zoomMeeting,
        formattedDate: format(meetingDateTime, 'EEEE, MMMM do, yyyy'),
        formattedTime: format(meetingDateTime, 'h:mm a'),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });

    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast({
        title: "Failed to schedule meeting",
        description: "There was an error creating your meeting. Please try again or contact support.",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
    })();
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">{t('zoomScheduler.title')}</h3>
        <p className="text-muted-foreground">
          Select your preferred date and time for a 30-minute consultation.
        </p>
      </div>

      <div className="space-y-4">
        {/* Date Selection */}
        <div className="space-y-2">
          <Label htmlFor="date">Select Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !selectedDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Time Selection */}
        <div className="space-y-2">
          <Label htmlFor="time">Select Time</Label>
          <div className="grid grid-cols-3 gap-2">
            {timeSlots.map((time) => (
              <Button
                key={time}
                variant={selectedTime === time ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedTime(time)}
                className="text-sm"
              >
                <Clock className="w-3 h-3 mr-1" />
                {time}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={isCreating}
          className="flex-1"
        >
          Back
        </Button>
        <Button
          onClick={handleScheduleMeeting}
          disabled={!selectedDate || !selectedTime || isCreating}
          className="flex-1"
        >
          {isCreating ? 'Scheduling...' : 'Schedule Meeting'}
        </Button>
      </div>
    </div>
  );
};

export default ZoomScheduler;